import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy } from '@angular/core';

/** Set on ion-app while any projected banner has height. */
export const SHELL_BANNERS_OPEN_CLASS = 'solo-shell-banners-open';
/** The rendered height of the strip, read by global.scss to push the routed page down. */
export const SHELL_BANNERS_HEIGHT_VAR = '--solo-shell-banners-height';

/**
 * The strip at the very top of the app shell that holds the session banners (impersonation, Tenant Sites
 * support member). ion-app is an ion-page (flex column, space-between) and ion-router-outlet covers it at
 * z-index 0, so a banner left in normal flow floats in the middle of the page over the content (AB#20366).
 *
 * This host is pinned to the top of ion-app and owns the top safe-area inset. It measures itself and publishes
 * its height on ion-app; global.scss moves the router outlet (and the enforcement banner) down by that height
 * and zeroes the outlet's own top inset, so the page header does not pad for the notch twice. With every
 * banner hidden the height is 0 and nothing moves.
 */
@Component({
  selector: 'app-shell-top-banners',
  template: `<ng-content></ng-content>`,
  styles: [`
    :host {
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      z-index: 11;
    }
    :host(.has-content) {
      padding-top: var(--ion-safe-area-top, env(safe-area-inset-top, 0px));
      padding-left: var(--ion-safe-area-left, env(safe-area-inset-left, 0px));
      padding-right: var(--ion-safe-area-right, env(safe-area-inset-right, 0px));
      background: var(--solo-session-banner-bg);
    }
  `]
})
export class ShellTopBannersComponent implements AfterViewInit, OnDestroy {
  private observer: ResizeObserver | null = null;
  private lastHeight = -1;

  constructor(private host: ElementRef<HTMLElement>, private zone: NgZone) {}

  ngAfterViewInit(): void {
    this.sync();
    if (typeof ResizeObserver === 'undefined') return;
    this.zone.runOutsideAngular(() => {
      this.observer = new ResizeObserver(() => this.sync());
      // Observe the projected banners too: the host's own size depends on the padding it adds once they show.
      this.observer.observe(this.host.nativeElement);
      for (const child of Array.from(this.host.nativeElement.children)) {
        this.observer.observe(child);
      }
    });
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    this.observer = null;
    const app = this.app();
    if (app) {
      app.classList.remove(SHELL_BANNERS_OPEN_CLASS);
      app.style.removeProperty(SHELL_BANNERS_HEIGHT_VAR);
    }
  }

  /** Publish the strip's height on ion-app; 0 when no banner is showing. */
  sync(): void {
    const host = this.host.nativeElement;
    // A banner with nothing to show renders an empty host element, so measure the projected children.
    const bannersHeight = Array.from(host.children)
      .reduce((total, child) => total + ((child as HTMLElement).offsetHeight || 0), 0);
    const hasContent = bannersHeight > 0;
    host.classList.toggle('has-content', hasContent);
    const height = hasContent ? host.offsetHeight : 0;
    if (height === this.lastHeight) return;
    this.lastHeight = height;

    const app = this.app();
    if (!app) return;
    if (height > 0) {
      app.style.setProperty(SHELL_BANNERS_HEIGHT_VAR, `${height}px`);
      app.classList.add(SHELL_BANNERS_OPEN_CLASS);
    } else {
      app.classList.remove(SHELL_BANNERS_OPEN_CLASS);
      app.style.removeProperty(SHELL_BANNERS_HEIGHT_VAR);
    }
  }

  private app(): HTMLElement | null {
    return this.host.nativeElement.closest('ion-app');
  }
}
